import type { PointMm } from '@besbello/design-core';

/**
 * ADAPTADOR MILIMETROS <-> PIXELES.
 *
 * P2 — El documento canonico esta SIEMPRE en milimetros; el pixel es una unidad
 * de pantalla. Esta es la unica pieza del cliente autorizada a convertir entre
 * los dos, y por eso vive sola y probada: cualquier conversion dispersa por los
 * componentes acabaria guardando pixeles en el documento.
 *
 * El lienzo muestra la ZONA DE MARCAJE completa con un margen, y la escala se
 * recalcula al girar el movil o cambiar de talla.
 */

export interface ViewportMm {
  /** Tamano de la zona de marcaje, en mm. */
  widthMm: number;
  heightMm: number;
}

export interface ViewportPx {
  widthPx: number;
  heightPx: number;
}

export interface MmPxAdapter {
  /** Pixeles de pantalla por milimetro. */
  scale: number;
  /** Desplazamiento en px del origen de la zona dentro del lienzo. */
  offsetXPx: number;
  offsetYPx: number;
  toPx(mm: number): number;
  toMm(px: number): number;
  pointToPx(point: PointMm): { x: number; y: number };
  pointToMm(point: { x: number; y: number }): PointMm;
}

/** Margen alrededor de la zona, en pixeles, para que se vea el borde. */
export const CANVAS_PADDING_PX = 16;

export function createAdapter(zone: ViewportMm, canvas: ViewportPx, paddingPx = CANVAS_PADDING_PX): MmPxAdapter {
  const usableWidth = Math.max(1, canvas.widthPx - paddingPx * 2);
  const usableHeight = Math.max(1, canvas.heightPx - paddingPx * 2);

  // La zona se muestra ENTERA: se escoge la escala que hace que quepa por el
  // lado mas restrictivo. Recortar la zona en pantalla haria que el cliente
  // colocase el diseno donde no se puede imprimir.
  const scale = Math.min(usableWidth / zone.widthMm, usableHeight / zone.heightMm);

  const offsetXPx = (canvas.widthPx - zone.widthMm * scale) / 2;
  const offsetYPx = (canvas.heightPx - zone.heightMm * scale) / 2;

  return {
    scale,
    offsetXPx,
    offsetYPx,
    toPx: (mm) => mm * scale,
    toMm: (px) => px / scale,
    pointToPx: (point) => ({ x: offsetXPx + point.x * scale, y: offsetYPx + point.y * scale }),
    pointToMm: (point) => ({ x: (point.x - offsetXPx) / scale, y: (point.y - offsetYPx) / scale }),
  };
}

/**
 * Redondeo de las medidas que se ENSENAN y se editan a mano: 0,1 mm. Mas
 * decimales en pantalla son ruido; menos, imprecision visible al colocar.
 */
export function roundMm(value: number): number {
  return Math.round(value * 10) / 10;
}

export function formatMm(value: number): string {
  return `${roundMm(value).toLocaleString('es-ES', { maximumFractionDigits: 1 })} mm`;
}

/**
 * Resolucion efectiva de una imagen colocada. Se calcula en el cliente para
 * avisar AL INSTANTE, pero la autoridad es `rules-core` en el servidor.
 */
export function effectiveDpiFor(intrinsicPx: number, placedMm: number): number {
  if (placedMm <= 0) return Infinity;
  return (intrinsicPx / placedMm) * 25.4;
}

/**
 * Tamano maximo en mm que puede ocupar una imagen sin bajar de un dpi dado. Es
 * el numero que permite ofrecer "ajustar al maximo con calidad" en vez de dejar
 * que el cliente estire hasta que salga un aviso.
 */
export function maxMmForDpi(intrinsicPx: number, minDpi: number): number {
  return (intrinsicPx / minDpi) * 25.4;
}
